/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useState } from 'react';

import type { NotificationModel } from '@/domain/models/notification.js';
import type { NotificationController } from '@/presentation/controllers/notification-controller.js';

type State = {
    notifications: NotificationModel[];
    unreadCount: number;
    total: number;
    page: number;
    totalPages: number;
    isLoading: boolean;
    error: string | null;
};

export function useNotifications(controller: NotificationController) {
    const [state, setState] = useState<State>({ notifications: [], unreadCount: 0, total: 0, page: 1, totalPages: 1, isLoading: true, error: null });
    const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
    const [page, setPage] = useState(1);

    const load = useCallback(async () => {
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const result = await controller.loadNotifications({ page, limit: 20, filter });
        result.fold(
            (error) => setState({ notifications: [], unreadCount: 0, total: 0, page: 1, totalPages: 1, isLoading: false, error: error.message }),
            (data) =>
                setState({
                    notifications: data.data,
                    unreadCount: data.unreadCount,
                    total: data.total,
                    page: data.page,
                    totalPages: data.totalPages,
                    isLoading: false,
                    error: null
                })
        );
    }, [controller, filter, page]);

    useEffect(() => {
        load();
    }, [load]);

    const markRead = useCallback(
        async (id: string) => {
            const result = await controller.markRead(id);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [controller, load]
    );

    const markAllRead = useCallback(async () => {
        const result = await controller.markAllRead();
        if (result.isRight()) {
            await load();
        }
        return result.isRight();
    }, [controller, load]);

    return { ...state, filter, setFilter, page, setPage, reload: load, markRead, markAllRead };
}
